import i18n from '@/i18n';
import { useTranslation } from 'react-i18next';

import { SectionHeader } from '@/components/SectionHeader';
import { useFormSyncState } from '@/hooks';
import { PATTERN_PORT_NAME, PROTOCOLS } from '@/utils';
import { Button, Card, Form, Grid, Input, InputNumber, Select } from '@arco-design/web-react';
import { IconDelete } from '@arco-design/web-react/icon';
import isEmpty from 'lodash/isEmpty';
import { useCallback, useEffect, useMemo, useRef } from 'react';
import styled from 'styled-components';
import { useDockerSettingStore } from '../docker-setting';
import { WorkloadComponent, WorkloadType } from '../types';
import { usePortSettingStore } from './usePortSettingStore';

const t = i18n.t.bind(i18n);
const Row = Grid.Row;
const Col = Grid.Col;
const FormItem = Form.Item;
const FormList = Form.List;
const { STATEFULSET } = WorkloadType;

interface PortSettingProps extends WorkloadComponent {
  hiddrenServicePort?: boolean;
}

const msg = {
  portSetting: t('components:port.settings'),
  description: t('components:set.ports.for.accessing.containers'),
  protocol: t('common:protocol'),
  name: t('common:name'),
  containerPort: t('common:container.port'),
  servicePort: t('common:service.port'),
  add: t('common:add'),
  invalidPortName: t(
    'components:name.can.only.contain.lowercase.letters.numbers.and.hyphens.must.start.with.a.lowercase.letter.and.end.with.a.lowercase.letter.or.number.maximum.15.characters',
  ),
};
export function PortSetting({ module, hiddrenServicePort }: PortSettingProps) {
  const { t } = useTranslation();
  const dockerSetting = useDockerSettingStore((state) => state.dockerSetting);
  const { ports, updatePorts, setValidate } = usePortSettingStore();
  const [form] = Form.useForm();
  useFormSyncState(form, { ports });
  const showServicePort =
    module === STATEFULSET && !hiddrenServicePort && dockerSetting.containerType === 'containers';

  const initialValues = useMemo(() => {
    if (isEmpty(ports)) {
      return {
        ports: undefined,
      };
    }
    return {
      ports,
    };
  }, [ports]);

  // 存在空值, 禁用添加按钮
  const disabledAdd = useMemo(() => {
    return ports?.some((item) => {
      return !item?.name || !item?.protocol || !item?.containerPort;
    });
  }, [ports]);

  // 自定义校验规则
  const portsValidator = (values, cb) => {
    // 若有一行名称为空，则报错
    if (
      values.some(
        (item) => !item.name || !PATTERN_PORT_NAME.test(item.name) || item.name?.length > 15,
      )
    ) {
      return cb(msg.invalidPortName);
    }
    // 若有一行端口为空,则报错
    if (values.some((item) => !item.containerPort)) {
      return cb(t('components:port.cannot.be.empty'));
    }

    return cb();
  };

  // 将 form.validate 二次封装为一个useCallback函数, 要求是返回一个Promise, 用于校验表单,成功校验 true 否则 false
  const validate = useCallback(() => {
    // 允许不添加端口
    if (isEmpty(ports)) {
      return Promise.resolve(true);
    }
    return form
      .validate()
      .then(() => true)
      .catch(() => false);
  }, [form, ports]);

  useEffect(() => {
    setValidate(validate);
  }, [validate, setValidate]);

  // 创建一个 ref 来存储新增行的第一个输入框
  const portInputRef = useRef<any>(null);
  const editRef = useRef<boolean>(false); // 防止初次渲染时聚焦到新增行的第一个输入框

  // useEffect 监听 ports 变化，当 ports 增加时，聚焦到新增行的第一个输入框
  useEffect(() => {
    if (editRef.current && ports?.length > 0 && portInputRef.current) {
      portInputRef.current.focus();
      editRef.current = false;
    }
  }, [ports]);

  return (
    <>
      <Card>
        <SectionHeader
          title={msg.portSetting || t('components:port.settings')}
          description={msg.description || t('components:set.ports.for.accessing.containers')}
        />
        <Box>
          <Row gutter={8}>
            <Col span={showServicePort ? 6 : 8}>
              <span>{msg.protocol || t('common:protocol')}</span>
            </Col>
            <Col span={showServicePort ? 6 : 8}>
              <span>{msg.name || t('common:name')}</span>
            </Col>
            <Col span={showServicePort ? 6 : 7}>
              <span>{msg.containerPort || t('common:container.port')}</span>
            </Col>
            {showServicePort && (
              <Col span={5}>
                <span>{msg.servicePort || t('common:service.port')}</span>
              </Col>
            )}
          </Row>
          <Form
            form={form}
            autoComplete="off"
            initialValues={initialValues}
            onValuesChange={(_, values) => {
              updatePorts(values.ports);
            }}
            validateMessages={{
              required: ' ', // 为了不显示默认的 required 错误信息
            }}
            // onBlur={validate}
          >
            <FormItem layout="vertical" noStyle>
              <FormList field="ports" rules={[{ required: true }, { validator: portsValidator }]}>
                {(fields, { add, remove, move }) => {
                  return (
                    <>
                      {fields.map((item, index) => {
                        return (
                          <Row gutter={8} key={item.key}>
                            <Col span={showServicePort ? 6 : 8}>
                              <Form.Item
                                field={`${item.field}.protocol`}
                                rules={[{ required: true }]}
                              >
                                <Select showSearch options={PROTOCOLS} allowClear />
                              </Form.Item>
                            </Col>
                            <Col span={showServicePort ? 6 : 8}>
                              <Form.Item
                                field={`${item.field}.name`}
                                rules={[{ required: true }]}
                                onBlur={validate}
                              >
                                <Input />
                              </Form.Item>
                            </Col>
                            <Col span={showServicePort ? 6 : 7}>
                              <Form.Item
                                field={`${item.field}.containerPort`}
                                rules={[{ required: true }]}
                                onBlur={validate}
                              >
                                <InputNumber
                                  ref={index === fields?.length - 1 ? portInputRef : null}
                                />
                              </Form.Item>
                            </Col>
                            {showServicePort && (
                              <Col span={5}>
                                <Form.Item
                                  field={`${item.field}.servicePort`}
                                  rules={[{ required: true }]}
                                  onBlur={validate}
                                >
                                  <InputNumber />
                                </Form.Item>
                              </Col>
                            )}
                            <Col span={1}>
                              <Button
                                icon={<IconDelete />}
                                shape="circle"
                                status="danger"
                                onClick={() => remove(index)}
                              />
                            </Col>
                          </Row>
                        );
                      })}
                      <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
                        <Button
                          type="primary"
                          disabled={disabledAdd}
                          onClick={() => {
                            editRef.current = true;
                            add({
                              name: `${'TCP'.toLowerCase()}-${ports?.length || 0}`,
                              protocol: 'TCP',
                              containerPort: undefined,
                            });
                          }}
                        >
                          {msg.add || t('common:add')}
                        </Button>
                      </div>
                    </>
                  );
                }}
              </FormList>
            </FormItem>
          </Form>
        </Box>
      </Card>
    </>
  );
}

/** ============================ 样式 ==================================== */
const Box = styled.div`
  background-color: #f9f9f9;
  padding: 1rem;
`;
